import { ChangeDetectionStrategy, Component, inject, Input } from '@angular/core'
import { isSelfEnvironment, SUPPORT_ENVIRONMENTS, SupportEnvironment } from '../support-environment'

/**
 * The one place an environment is named in a list of rows. Shared rather than left inside the table
 * cell because the lists that need it are not all paginated tables — the batch job list and Atlas's
 * own ledgers hand-roll their markup, and three copies of the shape is how they stop matching.
 *
 * Renders nothing for the environment serving the page: naming the only environment there is says
 * nothing, and the single-environment screens are these same components with a one-entry list.
 */
@Component({
  selector: 'app-environment-pill',
  standalone: true,
  templateUrl: './environment-pill.component.html',
  styleUrl: './environment-pill.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class EnvironmentPillComponent {
  private environments = inject(SUPPORT_ENVIRONMENTS)

  @Input({ required: true })
  environment: SupportEnvironment | null = null

  // Where the pill leads, for a list whose rows exist in that environment: naming the environment and
  // then landing the reader on its support home leaves them to find the row again by hand.
  @Input()
  href: string | null = null

  get named(): boolean {
    return this.environment !== null && !isSelfEnvironment(this.environment)
  }

  get hue(): number {
    return this.environment === null ? 0 : environmentHue(this.environment, this.environments)
  }

  get link(): string {
    return this.href ?? `${this.environment?.origin ?? ''}/app/support`
  }
}

const GOLDEN_ANGLE_DEGREES = 137.508

/**
 * Golden angle over the environment's position in the configured list: evenly spread for any list
 * this size, and appending an environment leaves the existing colours where they were.
 *
 * Hashing the key instead reads as more stable but guarantees no spacing at all — it put 'arendt' and
 * 'athora' half a degree apart, which is the same colour to a reader.
 */
export function environmentHue(environment: SupportEnvironment, environments: SupportEnvironment[]): number {
  const index = environments.findIndex((candidate) => candidate.key === environment.key)
  // An environment that is not in the list at all still gets a colour of its own rather than sharing
  // the first one's.
  return ((index === -1 ? environments.length : index) * GOLDEN_ANGLE_DEGREES) % 360
}
